import React, { useEffect } from 'react';
import {
    IconButton,
} from '@mui/material';
import {
    Maximize,
    Minimize,
    Monitor,
} from 'lucide-react';
import { useGetFrameByIdQuery } from '../../app/services/frameServices';
import { useGetScreenFrameConfigByIdQuery } from '../../app/services/screenFrameConfigService';
import ComponentPreview from '../frame-preview/SharedComponentPreview';
import { GetDefaultSize } from '../../common/constants';

// const marqueeStyle = `
//   @keyframes marquee {
//     0% { transform: translateX(100%); }
//     100% { transform: translateX(-100%); }
//   }
//   .animate-marquee {
//     animation: marquee linear infinite;
//   }
// `;
const marqueeStyle = ''

interface FramePreviewPlayerProps {
    frameId?: string | number | null;
    isOpen?: boolean;
    onClose?: () => void;
    screenFrameConfigId?: number;
}

const FullScreenPreview: React.FC<FramePreviewPlayerProps> = ({
}) => {
    const urlParams = new URLSearchParams(window.location.search);
    const frameId = urlParams.get('frameId');
    const screenFrameConfigId = urlParams.get('ScreenConfigId') ? Number(urlParams.get('ScreenConfigId')) : undefined;
    // Inject marquee CSS
    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.textContent = marqueeStyle;
        document.head.appendChild(styleElement);
        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    const {
        data: frameData,
        isLoading,
        isError,
    } = useGetFrameByIdQuery(
        frameId?.toString() || '',
        { skip: !frameId }
    );

    const {
        data: screenFrameConfigData
    } = useGetScreenFrameConfigByIdQuery(
        screenFrameConfigId || 0,
        { skip: !screenFrameConfigId }
    );

    const frame = frameData?.data;
    const components = frame?.components || [];
    const backgroundImage = screenFrameConfigData?.data?.backgroundImage || '';
    
    // Determine canvas dimensions based on frame orientation
    const canvasDimensions = GetDefaultSize(frame?.orientation || 'Landscape');

    const enterFullScreen = () => {
        if (document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen();
        }
    };
    const exitFullScreen = () => {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        }
    };


    if (isError) {
        return (
            <div style={{ textAlign: 'center', padding: '64px 0' }}>
                <p style={{ color: '#ef4444' }}>Failed to load frame preview</p>
            </div>
        );
    }
    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                width: '100%',
                backgroundColor: '#000000',
            }}>
                <div style={{ textAlign: 'center', color: 'white' }}>
                    <div style={{
                        width: 48,
                        height: 48,
                        border: '4px solid #333',
                        borderTop: '4px solid #fff',
                        borderRadius: '50%',
                        margin: '0 auto 16px',
                        animation: 'spin 1s linear infinite',
                    }} />
                    <p>Loading frame preview...</p>
                </div>
                <style>{`
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div style={{
            margin: 0,
            padding: 0,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            minHeight: 0,
            height: '100vh',
            width: '100%',
            backgroundColor: '#000000',
            position: 'relative',
        }}>
            <div style={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 9999,
                display: 'flex',
                justifyContent:'center',
                alignItems:"center",
                gap: 8,
            }}>
                <IconButton 
                    onClick={enterFullScreen} 
                    sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: 'white',
                        color: 'black',
                        '&:hover': {
                            backgroundColor: 'grey.100',
                        }
                    }}
                >
                    <Maximize size={24} />
                </IconButton>
                <IconButton 
                    onClick={exitFullScreen} 
                    sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: 'white',
                        color: 'black',
                        '&:hover': {
                            backgroundColor: 'grey.100',
                        }
                    }}
                >
                    <Minimize size={24} />
                </IconButton>
            </div>
            <div
                style={{
                    margin: 0,
                    padding: 0,
                    position: 'relative',
                    backgroundColor: 'white',
                    borderColor: '#d1d5db',
                    ...(frame?.orientation === 'Portrait' 
                        ? {
                            height: 'calc(100vh - 32px)',
                            width: 'auto',
                            maxWidth: '100vw',
                          }
                        : {
                            width: '100%',
                            height: 'auto',
                            maxHeight: 'calc(100vh - 32px)',
                          }
                    ),
                    aspectRatio: `${canvasDimensions.width}/${canvasDimensions.height}`,
                    backgroundImage: backgroundImage ? `url(${import.meta.env.VITE_API_DOMAIN}/files?fileName=${encodeURIComponent(backgroundImage)})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                {components.length === 0 ? (
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#9ca3af',
                        backgroundColor: '#000000',
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <Monitor size={64} style={{ marginBottom: 16, opacity: 0.5 }} />
                            <p>Empty Frame</p>
                        </div>
                    </div>
                ) : (
                    components.map((component, index) => (
                        <ComponentPreview
                            key={component.id || index}
                            component={component}
                            screenFrameConfigId={screenFrameConfigId || 0}
                            orientation={frame?.orientation}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

export default FullScreenPreview;